import { AudioPodClient } from '../client/AudioPodClient';
import { TranslationRequest, TranslationResult, Job } from '../types';

export class TranslationService {
  constructor(private client: AudioPodClient) {}

  async translateAudio(request: TranslationRequest): Promise<TranslationResult> {
    const { audioFile, targetLanguage, sourceLanguage, waitForCompletion = false, timeout = 900000 } = request;

    let fileData: File | Blob;
    if (typeof audioFile === 'string') {
      const fs = await import('fs');
      const fileBuffer = fs.readFileSync(audioFile);
      fileData = new Blob([fileBuffer], { type: 'audio/*' });
    } else {
      fileData = audioFile;
    }

    const formData: Record<string, any> = { target_language: targetLanguage };
    if (sourceLanguage) formData.source_language = sourceLanguage;

    const job = await this.client.uploadFile<Job>('/translation/translate/speech', fileData, formData);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        sourceLanguage: result.source_language,
        targetLanguage: result.target_language,
        audioOutputUrl: result.audio_output_path,
        videoOutputUrl: result.video_output_path,
        transcriptPath: result.transcript_path
      };
    }
    return { job };
  }

  async getTranslationJob(jobId: number): Promise<TranslationResult> {
    const job = await this.client.get<any>(`/translation/translations/${jobId}`);
    return {
      job,
      sourceLanguage: job.source_language,
      targetLanguage: job.target_language,
      audioOutputUrl: job.audio_output_path || job.translated_audio_url,
      videoOutputUrl: job.video_output_path || job.video_output_url,
      transcriptPath: job.transcript_path,
      transcriptUrls: job.transcript_urls
    };
  }

  async listTranslationJobs(options: {
    skip?: number;
    limit?: number;
  } = {}): Promise<TranslationResult[]> {
    const { skip = 0, limit = 50 } = options;
    
    const jobs = await this.client.get<any[]>('/translation/translations', {
      skip,
      limit
    });

    return jobs.map(job => ({
      job,
      sourceLanguage: job.source_language,
      targetLanguage: job.target_language,
      audioOutputUrl: job.audio_output_path || job.translated_audio_url,
      videoOutputUrl: job.video_output_path || job.video_output_url,
      transcriptPath: job.transcript_path,
      transcriptUrls: job.transcript_urls
    }));
  }

  async deleteTranslationJob(jobId: number): Promise<void> {
    await this.client.delete(`/translation/translations/${jobId}`);
  }
}
